import { AdminTimeRangePicker } from '@/components/AdminTimeRangePicker';
import { TimeRangeFilter } from '@/components/TimeRangeFilter';
import { financeCountryOptions } from '../lib/financeColors';

/** Shared by Finance chart cards — always bound to the page-level filter set. */
export type FinanceChartFilterProps = {
  range: string;
  onRangeChange: (range: string) => void;
  countryCode: string;
  onCountryCodeChange: (countryCode: string) => void;
  availableCountryCodes?: readonly string[];
  startDate?: string;
  endDate?: string;
  isLoading?: boolean;
};

/** Compact creator-country control for chart card headers (beside date range). */
export function FinanceChartCountryFilter({
  countryCode,
  onCountryCodeChange,
  availableCountryCodes = [],
  className,
  ariaLabel = 'Change creator country (Connect) filter',
}: {
  countryCode: string;
  onCountryCodeChange: (countryCode: string) => void;
  availableCountryCodes?: readonly string[];
  className?: string;
  ariaLabel?: string;
}) {
  const countryOptions = financeCountryOptions(availableCountryCodes);
  const countryOption = countryOptions.find((option) => option.value === countryCode) ?? {
    value: '',
    label: 'All creator countries',
    detail: 'Connect country buckets + not yet onboarded · not buyer geo',
  };

  return (
    <TimeRangeFilter
      size="compact"
      label={countryOption.label}
      detail="Creator country (Connect)"
      value={countryCode}
      options={countryOptions.map((option) => ({
        value: option.value,
        label: option.label,
      }))}
      onChange={onCountryCodeChange}
      ariaLabel={ariaLabel}
      className={className ?? 'w-full min-w-[200px] sm:w-[260px]'}
      title={countryOption.detail}
    />
  );
}

/**
 * Date + country pickers for a Finance chart card. Changing either updates
 * the whole Financial page (same state as the header toolbar).
 */
export function FinanceChartFilters({
  range,
  onRangeChange,
  startDate,
  endDate,
  countryCode,
  onCountryCodeChange,
  availableCountryCodes = [],
  dateAriaLabel = 'Change date range',
  countryAriaLabel = 'Change creator country (Connect) filter',
}: FinanceChartFilterProps & {
  dateAriaLabel?: string;
  countryAriaLabel?: string;
}) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <AdminTimeRangePicker
        preset="finance"
        value={range}
        onChange={onRangeChange}
        {...(startDate ? { startDate } : {})}
        {...(endDate ? { endDate } : {})}
        ariaLabel={dateAriaLabel}
        className="w-full min-w-[200px] sm:w-[260px]"
      />
      <FinanceChartCountryFilter
        countryCode={countryCode}
        onCountryCodeChange={onCountryCodeChange}
        availableCountryCodes={availableCountryCodes}
        ariaLabel={countryAriaLabel}
      />
    </div>
  );
}
